/**
 * Canonical file-type definitions for the entire frontend.
 *
 * Every component that needs to validate or filter files should import from
 * here. This is the frontend companion to
 * ``backend/src/constants/file_types.py``.
 *
 * When adding a new file type, update *both* this file and the backend module.
 */

// ---------------------------------------------------------------------------
// Upload extensions (data sources, projects, chat)
// ---------------------------------------------------------------------------

export const ALLOWED_UPLOAD_EXTENSIONS = new Set([
  // Documents
  ".pdf",
  ".doc",
  ".docx",
  ".ppt",
  ".pptx",
  ".xls",
  ".xlsx",
  // Text / structured
  ".txt",
  ".md",
  ".csv",
  ".json",
  ".vtt",
  // Images
  ".jpg",
  ".jpeg",
  ".png",
  ".gif",
  ".webp",
  // Audio
  ".mp3",
  ".wav",
  ".m4a",
  ".ogg",
  ".flac",
  ".webm",
  ".mp4",
  // Web / email
  ".html",
  ".htm",
  ".epub",
  ".msg",
  ".eml",
]);

/** Comma-separated accept string for <input type="file"> elements. */
export const UPLOAD_ACCEPT_STRING = [...ALLOWED_UPLOAD_EXTENSIONS].join(",");

export const MAX_FILES_PER_UPLOAD = 20;

export const CHAT_ATTACHMENT_EXTENSIONS = new Set([
  ".txt",
  ".md",
  ".csv",
  ".json",
  ".vtt",
  ".pdf",
  ".docx",
  ".doc",
  ".pptx",
  ".ppt",
  ".xlsx",
  ".xls",
  ".jpg",
  ".jpeg",
  ".png",
  ".gif",
  ".webp",
]);

export const CHAT_ATTACHMENT_ACCEPT_STRING = [...CHAT_ATTACHMENT_EXTENSIONS].join(",");

// ---------------------------------------------------------------------------
// Indexable extensions (everything the backend can process, minus images)
// ---------------------------------------------------------------------------

/** Extensions the backend indexing pipeline supports (for SharePoint / Google Drive browsers). */
export const INDEXABLE_EXTENSIONS = new Set(
  [...ALLOWED_UPLOAD_EXTENSIONS].filter((ext) => ![".jpg", ".jpeg", ".png", ".gif", ".webp"].includes(ext)),
);

// ---------------------------------------------------------------------------
// Image MIME types
// ---------------------------------------------------------------------------

export const ALLOWED_IMAGE_TYPES = new Set(["image/jpeg", "image/png", "image/gif", "image/webp"]);

// ---------------------------------------------------------------------------
// Human-readable label for upload modals
// ---------------------------------------------------------------------------

export const UPLOAD_EXTENSIONS_LABEL =
  "PDF, DOCX, PPTX, XLSX, TXT, MD, CSV, JSON, VTT, MP3, WAV, M4A, HTML, EPub, and email files";

// ---------------------------------------------------------------------------
// Large attachment hint (MHS-711)
// ---------------------------------------------------------------------------

/**
 * Text-heavy files above this size likely exceed what the backend will inline
 * into a chat message (about half the model's context window). The backend
 * truncates them with a note; the UI warns up front.
 */
export const LARGE_TEXT_ATTACHMENT_BYTES = 1024 * 1024;

const TEXT_HEAVY_EXTENSIONS = [".csv", ".tsv", ".txt", ".md", ".json", ".xlsx", ".xls", ".html", ".vtt"];

/** True when a file is text-heavy and large enough that only part of it may be read by the model. */
export function isLargeTextAttachment(file: { name: string; size: number; type: string }): boolean {
  if (ALLOWED_IMAGE_TYPES.has(file.type)) return false;
  const name = file.name.toLowerCase();
  return file.size > LARGE_TEXT_ATTACHMENT_BYTES && TEXT_HEAVY_EXTENSIONS.some((ext) => name.endsWith(ext));
}
